


































































































import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import {PropType} from 'vue'
import {$} from '@/facade'
import {OrbitProject} from '@/model/resource/OrbitProject'
import {getN3InvocationBuilder} from '@/model/wallets/invocationsBuilder'
import {OrbitFundProjectStep} from '@/enums/OrbitFundProjectStep'
import {NeoHelper} from '@/helpers/NeoHelper'
import OrbitLoadingTransactionStep from '@/components/orbit/OrbitLoadingTransactionStep.vue'
import OrbitInsufficientGasStep from '@/components/orbit/OrbitInsufficientGasStep.vue'
import OrbitUnexpectedErrorStep from '@/components/orbit/OrbitUnexpectedErrorStep.vue'
import OrbitRejectedByTheUserStep from '@/components/orbit/OrbitRejectedByTheUserStep.vue'
import OrbitSuccessStep from '@/components/orbit/OrbitSuccessStep.vue'
import {OrbitHelper} from '@/helpers/OrbitHelper'
import {currentN3Network} from '@/helpers/NetworkModeRegistry'

@Component({
  computed: {
    OrbitFundProjectStep() {
      return OrbitFundProjectStep
    },
  },
  components: {
    OrbitSuccessStep,
    OrbitLoadingTransactionStep,
    OrbitInsufficientGasStep,
    OrbitUnexpectedErrorStep,
    OrbitRejectedByTheUserStep,
  },
})
export default class OrbitFundProjectModal extends Vue {
  @Prop({type: Array as PropType<OrbitProject[] | null>})
  orbitProjects!: OrbitProject[] | null

  currentStep: OrbitFundProjectStep = OrbitFundProjectStep.CONNECTING_WALLET
  transactionHash: string | null = null

  mounted() {
    this.populateResources()
  }

  async populateResources() {
    await OrbitHelper.checkWalletPermissions().catch(() => {
      this.closeModal()
      return
    })

    await this.fundOrbit()
  }

  async fundOrbit() {
    try {
      if (!this.orbitProjects || this.orbitProjects.length === 0) {
        throw new Error('Orbit projects not found')
      }

      this.currentStep = OrbitFundProjectStep.WAITING_SIGNATURE

      const signingNetwork = currentN3Network()

      const n3InvocationBuilder = getN3InvocationBuilder()
      const fundOrbitPoolInvocationBuilder = n3InvocationBuilder.fundOrbitProjectsInvocation(
        this.orbitProjects.map(op => {
          if (
            !op.amount ||
            !op.tokenPriced ||
            !op.tokenPriced.hash ||
            !op.contract ||
            !op.contract.hash ||
            !op.instanceId
          ) {
            throw new Error('Missing parameters')
          }

          if (
            op.blockchainVersion?.networkIdentifier?.trim().toLowerCase() !==
            signingNetwork
          ) {
            throw new Error(
              `Orbit project ${op.contract.hash} does not belong to ${signingNetwork}`
            )
          }

          return {
            amount: String(op.amount),
            tokenScriptHash: op.tokenPriced.hash,
            projectScriptHash: op.contract.hash,
            instanceId: String(op.instanceId),
          }
        })
      )

      this.transactionHash = await $.walletAdapter.invokeN3Wallet(
        fundOrbitPoolInvocationBuilder
      )
      if (!this.transactionHash) throw new Error('Undefined transaction')

      this.currentStep = OrbitFundProjectStep.VALIDATING_TRANSACTION

      const isTransactionValid = await NeoHelper.validateTransaction(
        this.transactionHash
      )
      if (!isTransactionValid) throw new Error('Failed to verify transaction')

      const fundedKeys = await OrbitHelper.getFundedProjectKeys(
        this.transactionHash
      )
      const fundedProjects = this.orbitProjects.filter(op =>
        fundedKeys.has(
          OrbitHelper.projectKey(op.contract?.hash ?? '', op.instanceId ?? 0)
        )
      )
      const unfundedProjects = this.orbitProjects.filter(
        op => !fundedProjects.includes(op)
      )

      await Promise.all(
        fundedProjects.map(async op => {
          op.isFunded = true
          await op.persistOrbitProject()
        })
      )

      this.currentStep = OrbitFundProjectStep.FUNDED

      if (unfundedProjects.length) {
        $.toast.error(
          $.translate('components.OrbitFundProjectModal.partiallyFunded', {
            funded: fundedProjects.length,
            total: this.orbitProjects.length,
          }),
          '',
          false
        )
      } else {
        $.toast.success('system.success.persist')
      }
    } catch (error) {
      this.threatError(error)
    }

    this.closeModal()
  }

  closeModal() {
    if (
      this.currentStep === OrbitFundProjectStep.VALIDATING_TRANSACTION ||
      this.currentStep === OrbitFundProjectStep.WAITING_SIGNATURE
    ) {
      return
    }

    this.currentStep = OrbitFundProjectStep.WAITING_SIGNATURE
    this.$emit('close')
  }

  @Watch('$store.state.walletAdapter.n3.address')
  async onN3AddressChange(address: string | null) {
    if (!address) {
      this.closeModal()
      return
    }

    await OrbitHelper.checkWalletPermissions().catch(() => {
      this.closeModal()
    })
  }

  private threatError(error: any) {
    console.error(error)

    if (String(error.message).includes('project already funded')) {
      $.toast.error('Project already funded.')
    }

    const isNeonWalletRejected =
      error.message && String(error.message).includes('rejected by the user')

    const isNeoLineWalletRejected =
      error.description &&
      error.description === 'The user cancels, or refuses the dapps request'

    if (isNeonWalletRejected || isNeoLineWalletRejected) {
      this.currentStep = OrbitFundProjectStep.REJECTED_BY_THE_USER
      return
    }

    const hasInsufficientFunds =
      error.message && String(error.message).includes('insufficient funds')
    if (hasInsufficientFunds) {
      this.currentStep = OrbitFundProjectStep.INSUFFICIENT_GAS
      return
    }

    this.currentStep = OrbitFundProjectStep.UNKNOWN_ERROR
  }
}
